import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import useAdminPro from "app/universal/route/useAdminPro";
import { Modal, Badge, Spinner, Table, Row, Col } from "react-bootstrap";
import { getCustomer } from "state/customer/actions";
import { getClientSavHistory } from "state/sav/actions";
import { MOTIF_LABELS, STATUT_LABELS, GESTE_LABELS, labelFor } from "app/orderTool/pages/pipelineSav/savLabels";

/**
 * Fiche client en surcouche (LECTURE SEULE) : coordonnées + historique SAV
 * complet (réglés inclus). Ouverte depuis l'écran devis, la bannière
 * « SAV en cours » et les cartes du pipeline SAV.
 *
 * Props : show, clientId, entrepriseId (optionnel, pour l'historique société),
 * onClose, et onOpenSav(savId) — quand il est fourni, les lignes de
 * l'historique s'ouvrent au clic, la fiche restant dessous pour revenir à la
 * liste et en consulter une autre (retour Camille 17/09).
 */
const STATUT_VARIANT = {
  A_TRAITER: "danger",
  EN_COURS: "warning",
  REGLE: "success",
};

const ClientFicheModal = ({ show, clientId, entrepriseId, onClose, onOpenSav }) => {
  // Historique SAV : réservé aux admins pro, comme l'onglet SAV B2B.
  const canSav = useAdminPro();
  const dispatch = useDispatch();
  const [client, setClient] = useState(null);
  const [savs, setSavs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) {
      setClient(null);
      setSavs([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const [cli, hist] = await Promise.all([
        clientId ? dispatch(getCustomer(clientId)) : Promise.resolve(null),
        canSav ? dispatch(getClientSavHistory({ clientId, entrepriseId })) : Promise.resolve(null),
      ]);
      if (cancelled) return;
      // L'endpoint client renvoie l'objet directement
      setClient(cli && !cli.error ? cli : null);
      setSavs((hist?.savs || []).filter(Boolean));
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
    // canSav dans les dépendances : /api/me.json arrive de façon asynchrone,
    // l'accès peut passer de false à true après le premier rendu.
  }, [show, clientId, entrepriseId, canSav, dispatch]);

  const nomComplet = client
    ? [client.firstname, client.lastname].filter(Boolean).join(" ")
    : "—";

  const adresse = client
    ? [
        [client.street_number, client.street].filter(Boolean).join(" "),
        [client.postal_code, client.city].filter(Boolean).join(" "),
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  const enCours = savs.filter((s) => s.statut !== "REGLE").length;

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Fiche client
          {client && <span className="ml-2">— {nomComplet}</span>}
          {loading && <Spinner animation="border" size="sm" variant="success" className="ml-2" />}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: 160 }}>
        {!loading && !client && clientId && (
          <div className="text-muted">Client introuvable.</div>
        )}

        {client && (
          <>
            <h6>Coordonnées</h6>
            <Row className="mb-2">
              <Col md={6}>
                <small className="text-muted">Nom</small>
                <div>{nomComplet || "—"}</div>
              </Col>
              <Col md={6}>
                <small className="text-muted">Email</small>
                <div>{client.email || "—"}</div>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                <small className="text-muted">Téléphone</small>
                <div>{client.phone || "—"}</div>
              </Col>
              <Col md={6}>
                <small className="text-muted">Entreprise</small>
                <div>
                  {client.company || "—"}
                  {client.siret ? ` (SIRET ${client.siret})` : ""}
                </div>
              </Col>
            </Row>
            {adresse && (
              <Row className="mb-2">
                <Col md={12}>
                  <small className="text-muted">Adresse</small>
                  <div>{adresse}</div>
                </Col>
              </Row>
            )}
            <hr />
          </>
        )}

        {canSav && (
          <>
          <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 4 }}>
            <h6 className="mb-0">Historique SAV</h6>
            <div className="flex-shrink-0">
              <Badge variant="secondary" className="mr-1">{savs.length} au total</Badge>
              {enCours > 0 && <Badge variant="warning">{enCours} en cours</Badge>}
            </div>
          </div>

          {!loading && savs.length === 0 && (
            <div className="text-muted mt-2">Aucun SAV pour ce client.</div>
          )}

          {savs.length > 0 && (
            <div style={{ overflowX: "auto" }}>
            <Table size="sm" hover className="mt-2 mb-0">
              <thead>
                <tr>
                  <th>Référence</th>
                  <th>Statut</th>
                  <th>Motif</th>
                  <th>Type de geste</th>
                  <th>Commande</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {savs.map((s) => (
                  <tr
                    key={s.id}
                    onClick={onOpenSav ? () => onOpenSav(s.id) : undefined}
                    style={onOpenSav ? { cursor: "pointer" } : undefined}
                    title={onOpenSav ? "Ouvrir la carte de ce SAV" : undefined}
                  >
                    <td>{onOpenSav ? <strong>{s.reference}</strong> : s.reference}</td>
                    <td>
                      <Badge variant={STATUT_VARIANT[s.statut] || "secondary"}>
                        {labelFor(STATUT_LABELS, s.statut)}
                      </Badge>
                    </td>
                    <td>{labelFor(MOTIF_LABELS, s.motif)}</td>
                    <td>{s.type_geste ? labelFor(GESTE_LABELS, s.type_geste) : "—"}</td>
                    <td>
                      {s.commande_groupee_id ? (
                        <a
                          href={`/commande?order_id=${s.commande_groupee_id}&order_type=v2`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {s.commande_groupee_numero || "Voir"}
                        </a>
                      ) : (
                        s.commande_groupee_numero || (s.a_rattacher ? "À rattacher" : "—")
                      )}
                    </td>
                    <td>
                      {s.date_incident
                        ? new Date(s.date_incident).toLocaleDateString("fr-FR")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
          )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ClientFicheModal;
